import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { DashboardAnnualDaySlot } from '../../utils/dashboardAnnualDaySlots';
import { annualSlotHasTimetableProgramAssignment } from '../../utils/annualProgressSlotSource';
import {
  lessonMenuTitleParts,
  weeklyGuideLessonMenuTitleParts,
} from '../../utils/dashboardWeeklyLessonMenuTitle';
import {
  computeZoomAwareFixedPopupStyle,
  getLayoutViewportSize,
  PAGE_ZOOM_CHANGED_EVENT,
} from '../../utils/pageZoom';
import { DashboardLessonEvalStatusBadge } from './DashboardLessonEvalStatusBadge';

function isLessonMenuNoLesson(slot: DashboardAnnualDaySlot): boolean {
  if (slot.timetableSlotKind === 'out_of_term' || slot.timetableSlotKind === 'holiday') return true;
  return !annualSlotHasTimetableProgramAssignment(slot.timetableText, slot.timetableColorHex);
}

export type DashboardWeeklyLessonMenuTitleVariant = 'dashboard' | 'weekly-guide';

type Props = {
  slot: DashboardAnnualDaySlot;
  anchorRect: DOMRect;
  onClose: () => void;
  onOpenPresentation?: (slot: DashboardAnnualDaySlot) => void;
  /** 있으면 '바로가기' 버튼 노출: 해당 차시가 선택된 프로그램 페이지로 이동 */
  onOpenProgram?: (slot: DashboardAnnualDaySlot) => void;
  onOpenLessonRecord?: (slot: DashboardAnnualDaySlot) => void;
  onOpenBehaviorRecord?: (slot: DashboardAnnualDaySlot) => void;
  titleVariant?: DashboardWeeklyLessonMenuTitleVariant;
};

/** 위클리·캘린더 공통: 네비 줌에 맞춘 수업 미니 모달 */
const DashboardWeeklyLessonActionMenu: React.FC<Props> = ({
  slot,
  anchorRect,
  onClose,
  onOpenPresentation,
  onOpenProgram,
  onOpenLessonRecord,
  onOpenBehaviorRecord,
  titleVariant = 'dashboard',
}) => {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const isWeeklyGuide = titleVariant === 'weekly-guide';
  // 대시보드 미니 모달에는 단계별 학습계획 메모를 넣지 않는다(표시만, 데이터는 그대로)
  const dashboardTitle = lessonMenuTitleParts(slot, { hideLearningPlan: true });
  const guideTitle = weeklyGuideLessonMenuTitleParts(slot);
  const noLesson = isLessonMenuNoLesson(slot);
  const noLessonHint = '수업이 없는 블럭입니다';
  const estimatedHeight = isWeeklyGuide ? 300 : 250;
  /** 주간학습: 수업 카드를 가리지 않도록 카드 위쪽 경계에 우선 배치(공간 없으면 아래) */
  const menuStyle = computeZoomAwareFixedPopupStyle({
    anchorRect,
    preferredWidth: 260,
    estimatedHeight,
    gap: 6,
    pad: 8,
    zIndex: 2200,
    placement: isWeeklyGuide && anchorRect.top - 14 >= estimatedHeight ? 'above' : 'auto',
  });
  /** 화면 안에 들어가도록 높이를 제한(본문은 스크롤, 버튼은 항상 보이게) */
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);
  const placedTop = typeof menuStyle.top === 'number' ? menuStyle.top : null;
  const placedBottom = typeof menuStyle.bottom === 'number' ? menuStyle.bottom : null;
  useLayoutEffect(() => {
    const { height: vh } = getLayoutViewportSize();
    const pad = 8;
    const room = placedTop != null ? vh - placedTop - pad : placedBottom != null ? vh - placedBottom - pad : vh - pad * 2;
    setMaxHeight(Math.max(160, Math.floor(room)));
  }, [placedTop, placedBottom]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onPointer = (e: MouseEvent | PointerEvent) => {
      const t = e.target as Node | null;
      if (panelRef.current && t && !panelRef.current.contains(t)) onClose();
    };
    const onScrollDismiss = (e: Event) => {
      const t = e.target;
      if (
        t instanceof Element &&
        t.closest(
          '.cp-dash-weekly-lesson-menu, .cp-dash-weekly-cal-wrap, .cp-dash-weekly-wrap, .cp-weekly-learning-guide-table-wrap'
        )
      ) {
        return;
      }
      onClose();
    };
    const onDismiss = () => onClose();
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onPointer);
    window.addEventListener('scroll', onScrollDismiss, true);
    window.addEventListener('resize', onDismiss);
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, onDismiss);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onPointer);
      window.removeEventListener('scroll', onScrollDismiss, true);
      window.removeEventListener('resize', onDismiss);
      window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, onDismiss);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={panelRef}
      className={
        'cp-dash-weekly-lesson-menu' + (isWeeklyGuide ? ' cp-dash-weekly-lesson-menu--weekly-guide' : '')
      }
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-tutorial-dash="weekly-lesson-menu"
      style={maxHeight != null ? { ...menuStyle, maxHeight } : menuStyle}
    >
      <div className="cp-dash-weekly-lesson-menu-eval-wrap" aria-hidden>
        <DashboardLessonEvalStatusBadge
          code={slot.display.progressEvalCode}
          label={slot.display.progressEval}
        />
      </div>
      <div id={titleId} className="cp-dash-weekly-lesson-menu-title">
        {isWeeklyGuide ? (
          <>
            <div className="cp-dash-weekly-lesson-menu-title-head">{guideTitle.head}</div>
            {guideTitle.unit || guideTitle.page ? (
              <div className="cp-dash-weekly-lesson-menu-title-unit-page">
                <span className="cp-dash-weekly-lesson-menu-title-line">
                  {guideTitle.unit || (noLesson ? '수업 없음' : '')}
                </span>
                {guideTitle.page ? (
                  <span className="cp-dash-weekly-lesson-menu-title-page">{guideTitle.page}</span>
                ) : null}
              </div>
            ) : noLesson ? (
              <div className="cp-dash-weekly-lesson-menu-title-line">수업 없음</div>
            ) : null}
            {guideTitle.learning || guideTitle.periodFraction ? (
              <div className="cp-dash-weekly-lesson-menu-title-learning">
                {guideTitle.learning ? (
                  <span className="cp-dash-weekly-lesson-menu-title-line">{guideTitle.learning}</span>
                ) : null}
                {guideTitle.periodFraction ? (
                  <span className="cp-dash-weekly-lesson-menu-title-period">{guideTitle.periodFraction}</span>
                ) : null}
              </div>
            ) : null}
            <hr className="cp-dash-weekly-lesson-menu-title-rule" />
            <div className="cp-dash-weekly-lesson-menu-title-material">{guideTitle.material}</div>
          </>
        ) : (
          <>
            <div className="cp-dash-weekly-lesson-menu-title-head">{dashboardTitle.head}</div>
            {dashboardTitle.unit || dashboardTitle.page ? (
              <div className="cp-dash-weekly-lesson-menu-title-unit-page">
                <span className="cp-dash-weekly-lesson-menu-title-line">{dashboardTitle.unit}</span>
                {dashboardTitle.page ? (
                  <span className="cp-dash-weekly-lesson-menu-title-page">{dashboardTitle.page}</span>
                ) : null}
              </div>
            ) : noLesson ? (
              <div className="cp-dash-weekly-lesson-menu-title-line">수업 없음</div>
            ) : null}
            {dashboardTitle.learning ? (
              <div className="cp-dash-weekly-lesson-menu-title-line">{dashboardTitle.learning}</div>
            ) : null}
          </>
        )}
      </div>
      {onOpenProgram ? (
        <div className="cp-dash-weekly-lesson-menu-row cp-dash-weekly-lesson-menu-row--primary">
          <button
            type="button"
            className="cp-dash-weekly-lesson-menu-btn cp-dash-weekly-lesson-menu-btn--shortcut"
            disabled={noLesson}
            title={noLesson ? noLessonHint : '해당 차시가 선택된 프로그램 페이지로 이동'}
            onClick={() => {
              if (noLesson) return;
              onOpenProgram(slot);
              onClose();
            }}
          >
            바로가기
          </button>
          <button
            type="button"
            className="cp-dash-weekly-lesson-menu-btn is-primary"
            disabled={noLesson}
            title={noLesson ? noLessonHint : undefined}
            onClick={() => {
              if (noLesson) return;
              onOpenPresentation?.(slot);
              onClose();
            }}
          >
            수업 프레젠테이션
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="cp-dash-weekly-lesson-menu-btn is-primary"
          disabled={noLesson}
          title={noLesson ? noLessonHint : undefined}
          onClick={() => {
            if (noLesson) return;
            onOpenPresentation?.(slot);
            onClose();
          }}
        >
          수업 프레젠테이션
        </button>
      )}
      <div className="cp-dash-weekly-lesson-menu-row">
        <button
          type="button"
          className="cp-dash-weekly-lesson-menu-btn"
          disabled={noLesson}
          title={noLesson ? noLessonHint : undefined}
          onClick={() => {
            if (noLesson) return;
            onOpenLessonRecord?.(slot);
            onClose();
          }}
        >
          수업기록
        </button>
        <button
          type="button"
          className="cp-dash-weekly-lesson-menu-btn"
          onClick={() => {
            onOpenBehaviorRecord?.(slot);
            onClose();
          }}
        >
          행동기록
        </button>
      </div>
    </div>,
    document.body
  );
};

export default DashboardWeeklyLessonActionMenu;
